// Phase 35c — AI Copilot v2 (streaming + tool cards + quick chips).
//
// Major upgrade from Phase 33's static chat:
//   • Streams replies token-by-token via /messages/stream (no spinner +
//     wait — text appears progressively).
//   • Interleaves tool-call cards into the assistant bubble as the agentic
//     loop fires tools (see ToolCard.jsx).
//   • Proactive context-aware suggestion chips on empty state.
//   • Persists tool_calls on the assistant message so reloads re-render
//     them.
//   • Falls back transparently to the non-streaming endpoint if the SSE
//     transport throws (e.g. corporate proxy strips chunked encoding).
//
// Backend wire contract: see /app/backend/llm/streaming.py + the
// _gen() function inside server.py POST /copilot/.../messages/stream.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Sparkles, Send, Plus, Trash2, Link2, Bot, User as UserIcon, Loader2,
  ExternalLink, Unlink, Cpu,
} from "lucide-react";
import api from "@/lib/api";
import { streamCopilot } from "@/lib/copilotStream";
import ToolCard from "@/components/copilot/ToolCard";
import SuggestionChips from "@/components/copilot/SuggestionChips";

function timeFmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// One assistant message bubble — renders tool cards inline above the
// streamed/static text. Tool cards live on `msg.tool_calls` (persisted)
// or `msg.live_tool_calls` (in-flight; replaced on done).
function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const tools = msg.tool_calls || msg.live_tool_calls || [];
  return (
    <div
      className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`msg-${msg.role}`}
    >
      {!isUser && (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
          <Bot className="h-4 w-4" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
          isUser
            ? "bg-indigo-600 text-white"
            : "border border-slate-200 bg-white text-slate-800"
        }`}
      >
        {!isUser && tools.length > 0 && (
          <div className="mb-2" data-testid="msg-tool-calls">
            {tools.map((t, i) => (
              <ToolCard
                key={`${t.name}-${t.iteration ?? i}`}
                name={t.name}
                args={t.args}
                result={t.result}
                iteration={t.iteration}
              />
            ))}
          </div>
        )}
        <div className="whitespace-pre-wrap">
          {msg.content}
          {msg.streaming && <span className="ml-0.5 inline-block h-3 w-1 animate-pulse bg-indigo-500 align-middle" data-testid="msg-cursor" />}
        </div>
        {msg.ai_status && msg.ai_status !== "ok" && !msg.streaming && (
          <p className="mt-2 text-[10px] uppercase tracking-wide text-amber-600">
            ⚠ {msg.ai_status.replace(/_/g, " ")}
          </p>
        )}
        <div className={`mt-1 flex items-center gap-2 text-[10px] ${isUser ? "text-indigo-100" : "text-slate-400"}`}>
          {timeFmt(msg.created_at)}
          {msg.model_used && (
            <span className="inline-flex items-center gap-0.5">
              <Cpu className="h-2.5 w-2.5" /> {msg.model_used}
            </span>
          )}
        </div>
      </div>
      {isUser && (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-700">
          <UserIcon className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}

function ThinkingDots({ phase }) {
  return (
    <div className="flex gap-3" data-testid="msg-thinking">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
        <Bot className="h-4 w-4" />
      </div>
      <div
        data-testid="thinking-dots"
        className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
      >
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" style={{ animationDelay: "0ms" }} />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" style={{ animationDelay: "150ms" }} />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" style={{ animationDelay: "300ms" }} />
        {phase && (
          <span className="ml-1 text-[11px] italic text-slate-500">{phase}…</span>
        )}
      </div>
    </div>
  );
}

export default function Copilot() {
  const [params, setParams] = useSearchParams();
  const queryDealId = params.get("deal_id");
  const queryConvId = params.get("conv");

  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [deals, setDeals] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState(null);  // "thinking" | null
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load deals + conversations on mount.
  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([api.get("/copilot/conversations"), api.get("/deals")]);
        setConversations(c.data);
        setDeals(d.data);
        if (queryDealId) {
          await newConversation(queryDealId);
          params.delete("deal_id");
          setParams(params, { replace: true });
        } else if (queryConvId) {
          // Phase 35d — deep-link from Compare page's "Ask Copilot" CTA.
          await selectConversation(queryConvId);
          // Restore the stashed opener (set by AIInsightPanel before nav).
          const opener = sessionStorage.getItem(`copilot:opener:${queryConvId}`);
          if (opener) {
            setInput(opener);
            sessionStorage.removeItem(`copilot:opener:${queryConvId}`);
            setTimeout(() => inputRef.current?.focus(), 120);
          }
          params.delete("conv");
          setParams(params, { replace: true });
        } else if (c.data.length > 0) {
          await selectConversation(c.data[0].id);
        }
      } catch {
        toast.error("Failed to load Copilot.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for Cmd+K → "Ask Copilot" and "Continue last Copilot conversation".
  useEffect(() => {
    const onAsk = () => { newConversation(); setTimeout(() => inputRef.current?.focus(), 80); };
    const onContinue = async () => {
      try {
        const { data } = await api.get("/copilot/conversations");
        setConversations(data);
        if (data.length > 0) await selectConversation(data[0].id);
        else newConversation();
        setTimeout(() => inputRef.current?.focus(), 80);
      } catch { /* ignore */ }
    };
    window.addEventListener("runidea:copilot-new", onAsk);
    window.addEventListener("runidea:copilot-continue", onContinue);
    return () => {
      window.removeEventListener("runidea:copilot-new", onAsk);
      window.removeEventListener("runidea:copilot-continue", onContinue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll on every change (token streams, new messages, phase changes).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [active?.messages, phase, sending]);

  const selectConversation = async (id) => {
    try {
      const { data } = await api.get(`/copilot/conversations/${id}`);
      setActive(data);
    } catch {
      toast.error("Failed to load conversation.");
    }
  };

  const newConversation = async (dealId = null) => {
    try {
      const { data } = await api.post("/copilot/conversations", { deal_id: dealId });
      setConversations((cs) => [data, ...cs]);
      setActive({ ...data, messages: [] });
    } catch {
      toast.error("Failed to create conversation.");
    }
  };

  // Streaming send — the heart of Phase 35c.
  const sendMessage = useCallback(async (content) => {
    if (!active || !content.trim() || sending) return;
    setSending(true);
    setPhase("thinking");
    setInput("");

    const nowIso = new Date().toISOString();
    // Phase 36 Batch 2 — stamp a stable client-side `id` on every
    // message at creation time so the message list (Copilot.jsx:513)
    // can use `key={m.id}` instead of `key={i}`. Without this, React
    // mis-reconciles streaming token updates whenever the array shifts
    // (e.g. branch / regenerate / delete).
    const newId = () => (
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `msg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
    );
    const userMsg = { id: newId(), role: "user", content, created_at: nowIso };
    // Optimistic user message + placeholder assistant message we'll mutate
    // as tokens stream in.
    const assistantPlaceholder = {
      id: newId(),
      role: "assistant",
      content: "",
      streaming: true,
      live_tool_calls: [],
      created_at: nowIso,
    };
    setActive((c) => ({
      ...c,
      messages: [...(c.messages || []), userMsg, assistantPlaceholder],
    }));

    try {
      await streamCopilot({
        conversationId: active.id,
        content,
        onPhase: (p) => setPhase(p),
        onToolCallStart: (tc) => {
          setActive((c) => {
            const msgs = [...c.messages];
            const last = msgs[msgs.length - 1];
            const tools = [...(last.live_tool_calls || []), {
              iteration: tc.iteration, name: tc.name, args: tc.args,
              result: undefined,  // pending
            }];
            msgs[msgs.length - 1] = { ...last, live_tool_calls: tools };
            return { ...c, messages: msgs };
          });
        },
        onToolCallResult: (tc) => {
          setActive((c) => {
            const msgs = [...c.messages];
            const last = msgs[msgs.length - 1];
            const tools = (last.live_tool_calls || []).map((t) =>
              t.iteration === tc.iteration && t.name === tc.name
                ? { ...t, result: tc.result }
                : t,
            );
            msgs[msgs.length - 1] = { ...last, live_tool_calls: tools };
            return { ...c, messages: msgs };
          });
        },
        onToken: (chunk) => {
          setActive((c) => {
            const msgs = [...c.messages];
            const last = msgs[msgs.length - 1];
            msgs[msgs.length - 1] = { ...last, content: (last.content || "") + chunk };
            return { ...c, messages: msgs };
          });
        },
        onDone: (metadata) => {
          setActive((c) => {
            const msgs = [...c.messages];
            const last = msgs[msgs.length - 1];
            msgs[msgs.length - 1] = {
              ...last,
              streaming: false,
              tool_calls: last.live_tool_calls || [],
              live_tool_calls: undefined,
              ai_status: metadata.ai_status || "ok",
              model_used: metadata.model_used,
              created_at: new Date().toISOString(),
            };
            return { ...c, messages: msgs };
          });
        },
      });
      // Refresh title in sidebar after first message.
      const list = await api.get("/copilot/conversations");
      setConversations(list.data);
    } catch (err) {
      // SSE transport failure — fall back to the non-stream endpoint so the
      // user still gets an answer (degraded UX, not a dead-end).
      // eslint-disable-next-line no-console
      console.warn("Copilot stream failed, falling back:", err?.message);
      toast.info("Streaming unavailable — using fallback.");
      try {
        const { data } = await api.post(`/copilot/conversations/${active.id}/messages`, { content });
        setActive((c) => {
          const msgs = [...c.messages];
          // Drop the streaming placeholder and append the real assistant.
          msgs.pop();
          msgs.push(data.assistant_message);
          return { ...c, messages: msgs };
        });
      } catch {
        toast.error("Failed to send message.");
        setActive((c) => ({
          ...c,
          // Drop both the optimistic user msg AND the placeholder assistant.
          messages: (c.messages || []).slice(0, -2),
        }));
      }
    } finally {
      setSending(false);
      setPhase(null);
    }
  }, [active, sending]);

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/copilot/conversations/${id}`);
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (active?.id === id) setActive(null);
      setPendingDeleteId(null);
      toast.success("Conversation deleted.");
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const changeLinkedDeal = async (value) => {
    if (!active) return;
    const newDealId = value === "__none__" ? null : value;
    try {
      await newConversation(newDealId);
      toast.success(newDealId ? "Linked to new deal." : "Started a standalone conversation.");
    } catch {
      toast.error("Failed to switch deal.");
    }
  };

  const linkedDeal = useMemo(
    () => (active?.deal_id ? deals.find((d) => d.id === active.deal_id) : null),
    [active, deals],
  );

  const hasMessages = (active?.messages || []).length > 0;

  return (
    <AppShell>
      <div className="grid h-[calc(100vh-9rem)] gap-6 lg:grid-cols-[280px_1fr]" data-testid="copilot-root">
        {/* Sidebar */}
        <aside className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <Button
              onClick={() => newConversation()}
              data-testid="copilot-new-conversation"
              className="w-full bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              <Plus className="mr-2 h-4 w-4" /> New conversation
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {conversations.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-slate-500">
                No conversations yet. Start one above.
              </p>
            ) : (
              <ul className="space-y-1">
                {conversations.map((c) => {
                  const linkedAddr = c.deal_id ? deals.find((d) => d.id === c.deal_id)?.property?.address : null;
                  const isActive = active?.id === c.id;
                  return (
                    <li key={c.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => selectConversation(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectConversation(c.id);
                          }
                        }}
                        data-testid={`conv-${c.id}`}
                        className={`group flex w-full cursor-pointer items-start gap-2 rounded-lg px-3 py-2 text-left transition-colors ${
                          isActive ? "bg-indigo-50 text-indigo-900" : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-900">{c.title || "New conversation"}</p>
                          <p className="truncate text-xs text-slate-500">{c.last_message_preview || "—"}</p>
                          {linkedAddr && (
                            <p className="mt-1 inline-flex items-center gap-1 truncate text-[10px] text-indigo-600">
                              <Link2 className="h-2.5 w-2.5" /> {linkedAddr}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          aria-label="Delete conversation"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDeleteId(c.id);
                          }}
                          data-testid={`conv-delete-${c.id}`}
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Main chat */}
        <main className="flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
          {!active ? (
            <div className="flex flex-1 flex-col items-center justify-center p-10 text-center" data-testid="copilot-empty">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <Sparkles className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">runidea Copilot</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                A real co-pilot — it runs sensitivity, finds similar deals, queries
                your portfolio, and quotes live market data. Link a deal and watch
                it work.
              </p>
              <Button
                onClick={() => newConversation()}
                data-testid="copilot-start-new"
                className="mt-6 bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
              >
                <Plus className="mr-2 h-4 w-4" /> Start a new conversation
              </Button>
            </div>
          ) : (
            <>
              {/* Header */}
              <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold text-slate-900" data-testid="copilot-title">
                    {active.title || "New conversation"}
                  </h2>
                  {linkedDeal && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">
                        <Link2 className="h-3 w-3" /> Linked: {linkedDeal.property?.address}
                      </span>
                      <Link
                        to={`/deals/${linkedDeal.id}`}
                        target="_blank"
                        className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline"
                        data-testid="copilot-open-deal"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select onValueChange={changeLinkedDeal} value={active.deal_id || "__none__"}>
                    <SelectTrigger className="h-9 w-[220px] text-xs" data-testid="copilot-deal-select">
                      <SelectValue placeholder="Switch deal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        <span className="inline-flex items-center gap-1">
                          <Unlink className="h-3 w-3" /> No deal (standalone)
                        </span>
                      </SelectItem>
                      {deals.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.property?.address || "Unnamed deal"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </header>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-slate-50/30 p-4 sm:p-6" data-testid="copilot-messages">
                {!hasMessages && (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="copilot-empty-state">
                    Ask your first question below — or pick a starting point.
                  </div>
                )}
                {(active.messages || []).map((m, i) => (
                  // Phase 36 Batch 2 — stable key. Server-loaded messages
                  // expose `m.id`; optimistic / streaming messages get one
                  // stamped at send time (line ~239). Fall back to
                  // `${role}-${created_at}-${i}` for any legacy / pre-id
                  // history still in cache.
                  <MessageBubble
                    key={m.id || `${m.role}-${m.created_at || ""}-${i}`}
                    msg={m}
                  />
                ))}
                {/* Show "thinking" only while the assistant placeholder
                    has no tokens AND no tool calls yet. */}
                {sending && (active.messages || []).slice(-1)[0]?.content === "" &&
                  ((active.messages || []).slice(-1)[0]?.live_tool_calls || []).length === 0 && (
                    <ThinkingDots phase={phase} />
                  )}
              </div>

              {/* Suggestion chips + input */}
              <div className="border-t border-slate-200 p-4">
                <SuggestionChips
                  hasDealFocus={!!active.deal_id}
                  hasMessages={hasMessages}
                  dealId={active.deal_id || null}
                  onPick={(p) => { setInput(p); setTimeout(() => inputRef.current?.focus(), 30); }}
                />
                <form
                  onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                  className="flex items-end gap-2"
                >
                  <Textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(input);
                      }
                    }}
                    placeholder="Ask anything about real estate investing…"
                    rows={2}
                    data-testid="copilot-input"
                    className="min-h-[2.75rem] resize-none"
                  />
                  <Button
                    type="submit"
                    disabled={sending || !input.trim()}
                    data-testid="copilot-send"
                    className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </div>
            </>
          )}
        </main>
      </div>

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(o) => !o && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the conversation.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conv-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConversation(pendingDeleteId)}
              data-testid="conv-confirm-delete"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
